import { supabase } from './supabase';

interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

const VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
  INP: { good: 200, poor: 500 },
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = VITALS_THRESHOLDS[name as keyof typeof VITALS_THRESHOLDS];
  if (!thresholds) return 'good';

  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

async function sendToAnalytics(metric: WebVitalMetric) {
  try {
    const { error } = await supabase.from('web_vitals').insert({
      metric_name: metric.name,
      metric_value: metric.value,
      rating: metric.rating,
      page_url: window.location.pathname,
      user_agent: navigator.userAgent,
      connection_type: (navigator as any).connection?.effectiveType || 'unknown',
    });

    if (error) {
      console.error('Failed to log web vital:', error);
    }
  } catch (err) {
    console.error('Error sending web vital to analytics:', err);
  }
}

export function reportWebVitals() {
  if (typeof window === 'undefined') return;

  if ('web-vitals' in window) {
    return;
  }

  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          const metric: WebVitalMetric = {
            name: 'LCP',
            value: entry.startTime,
            rating: getRating('LCP', entry.startTime),
            delta: entry.startTime,
            id: `${entry.entryType}-${entry.startTime}`,
          };
          sendToAnalytics(metric);
        }

        if (entry.entryType === 'first-input') {
          const fidEntry = entry as PerformanceEventTiming;
          const value = fidEntry.processingStart - fidEntry.startTime;
          const metric: WebVitalMetric = {
            name: 'FID',
            value,
            rating: getRating('FID', value),
            delta: value,
            id: `${entry.entryType}-${entry.startTime}`,
          };
          sendToAnalytics(metric);
        }

        if (entry.entryType === 'layout-shift' && !(entry as any).hadRecentInput) {
          const clsEntry = entry as LayoutShift;
          const metric: WebVitalMetric = {
            name: 'CLS',
            value: clsEntry.value,
            rating: getRating('CLS', clsEntry.value),
            delta: clsEntry.value,
            id: `${entry.entryType}-${entry.startTime}`,
          };
          sendToAnalytics(metric);
        }

        if (entry.entryType === 'paint' && entry.name === 'first-contentful-paint') {
          const metric: WebVitalMetric = {
            name: 'FCP',
            value: entry.startTime,
            rating: getRating('FCP', entry.startTime),
            delta: entry.startTime,
            id: `${entry.entryType}-${entry.startTime}`,
          };
          sendToAnalytics(metric);
        }
      }
    });

    observer.observe({
      entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift', 'paint'],
    });

    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const ttfb = navEntry.responseStart - navEntry.requestStart;
      const metric: WebVitalMetric = {
        name: 'TTFB',
        value: ttfb,
        rating: getRating('TTFB', ttfb),
        delta: ttfb,
        id: `ttfb-${navEntry.startTime}`,
      };
      sendToAnalytics(metric);
    }
  } catch (error) {
    console.error('Error setting up web vitals:', error);
  }
}

export function initWebVitalsTracking() {
  if (typeof window === 'undefined') return;

  if (document.readyState === 'complete') {
    reportWebVitals();
  } else {
    window.addEventListener('load', reportWebVitals);
  }
}
